import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, ExternalLink, Copy, Check, Edit2, Trash2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function FirmDetailPage() {
  const { site_url } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [firm, setFirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [outcome, setOutcome] = useState('interested');
  const [nextFollowUpDate, setNextFollowUpDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [chartDays, setChartDays] = useState(7); // 7 or 30 days
  const [copiedField, setCopiedField] = useState(null); // Track which field was copied
  const [editingFollowUp, setEditingFollowUp] = useState(null); // Track which follow-up is being edited
  const [deleteConfirmId, setDeleteConfirmId] = useState(null); // Track which follow-up to delete

  // Copy to clipboard function
  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  useEffect(() => {
    fetchFirmDetail();
  }, [site_url]);

  const fetchFirmDetail = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/firms/${encodeURIComponent(site_url)}`, {
        withCredentials: true
      });
      setFirm(data);
    } catch (error) {
      if (error.response?.status === 403) {
        alert('Access denied to this firm');
        navigate('/dashboard');
      }
      console.error('Failed to fetch firm detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFollowUp = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      if (editingFollowUp) {
        // Update existing follow-up
        await axios.patch(
          `${API_URL}/api/follow-ups/${editingFollowUp._id}`,
          {
            note: note.trim(),
            outcome,
            next_follow_up_date: nextFollowUpDate || null
          },
          { withCredentials: true }
        );
        setEditingFollowUp(null);
      } else {
        // Create new follow-up
        await axios.post(
          `${API_URL}/api/firms/${encodeURIComponent(site_url)}/follow-ups`,
          {
            note: note.trim(),
            outcome,
            next_follow_up_date: nextFollowUpDate || null
          },
          { withCredentials: true }
        );
      }
      
      // Reset form
      setNote('');
      setOutcome('interested');
      setNextFollowUpDate('');
      
      // Refresh firm data
      fetchFirmDetail();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditFollowUp = (followUp) => {
    setEditingFollowUp(followUp);
    setNote(followUp.note);
    setOutcome(followUp.outcome);
    setNextFollowUpDate(followUp.next_follow_up_date || '');
    setError('');
    // Scroll to form
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingFollowUp(null);
    setNote('');
    setOutcome('interested');
    setNextFollowUpDate('');
    setError('');
  };

  const handleDeleteFollowUp = async (followUpId) => {
    try {
      await axios.delete(
        `${API_URL}/api/follow-ups/${followUpId}`,
        { withCredentials: true }
      );
      setDeleteConfirmId(null);
      fetchFirmDetail();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to delete follow-up');
    }
  };

  const canEditDelete = (followUp) => {
    // Check if current user is the creator
    const userId = user.user_id || user._id;  // Support both auth types
    if (followUp.created_by_user_id !== userId) return false;
    
    // Check if within 7 days
    const createdAt = new Date(followUp.created_at);
    const daysSince = Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24));
    return daysSince <= 7;
  };

  const getTrendBadgeStyle = (trend) => {
    switch (trend) {
      case 'Zero Activity':
        return 'bg-red-50 text-red-900 border-red-900';
      case 'Significant Increase':
        return 'bg-green-900 text-white border-green-900';
      case 'Increase':
        return 'bg-green-100 text-green-900 border-green-900';
      case 'Same':
        return 'bg-[#F4F4F5] text-[#52525B] border-[#52525B]';
      case 'Decrease':
        return 'bg-orange-100 text-orange-900 border-orange-900';
      case 'New':
        return 'bg-[#D1FAE5] text-[#065F46] border-[#065F46]';
      default:
        return 'bg-[#F4F4F5] text-[#52525B] border-[#52525B]';
    }
  };

  // Fill in missing dates with 0 event counts
  // Smart logic: Start from allocation date if trial is recent
  const fillMissingDates = (snapshots, days) => {
    if (!snapshots || snapshots.length === 0) return [];
    
    const today = new Date();
    let startDate = new Date(today);
    startDate.setDate(today.getDate() - days + 1);
    
    // If firm has allocated_on date, adjust start date
    if (firm.allocated_on) {
      const allocatedDate = new Date(firm.allocated_on);
      const daysSinceAllocation = Math.floor((today - allocatedDate) / (1000 * 60 * 60 * 24));
      
      // If allocated less than 'days' ago, start chart from allocation date
      if (daysSinceAllocation < days && daysSinceAllocation >= 0) {
        startDate = new Date(allocatedDate);
        // Adjust days to show from allocation to today
        days = daysSinceAllocation + 1;
      }
    }
    
    const filledData = [];
    const snapshotMap = {};
    
    // Create a map of existing snapshots
    snapshots.forEach(snap => {
      snapshotMap[snap.snapshot_date] = snap.event_count;
    });
    
    // Fill in all dates from startDate to today
    for (let i = 0; i < days; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const dateStr = currentDate.toISOString().split('T')[0];
      
      filledData.push({
        snapshot_date: dateStr,
        event_count: snapshotMap[dateStr] || 0
      });
    }
    
    return filledData;
  };

  // Calculate total events for visible chart data
  const calculateVisibleTotal = (snapshots, days) => {
    if (!snapshots || snapshots.length === 0) return 0;
    
    const filledData = fillMissingDates(snapshots, days);
    return filledData.reduce((sum, snap) => sum + snap.event_count, 0);
  };

  const getOutcomeBadgeStyle = (outcome) => {
    switch (outcome) {
      case 'interested':
        return 'bg-green-100 text-green-900 border-green-900';
      case 'purchased':
        return 'bg-[#0C9168] text-white border-[#0C9168]';
      case 'cold':
        return 'bg-blue-100 text-blue-900 border-blue-900';
      case 'no_response':
        return 'bg-[#F4F4F5] text-[#52525B] border-[#52525B]';
      case 'extended':
        return 'bg-purple-100 text-purple-900 border-purple-900';
      case 'deactivated':
        return 'bg-red-100 text-red-900 border-red-900';
      default:
        return 'bg-[#F4F4F5] text-[#52525B] border-[#52525B]';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center text-[#52525B] py-8">Loading...</div>
      </Layout>
    );
  }

  if (!firm) {
    return (
      <Layout>
        <div className="text-center text-[#52525B] py-8">Firm not found</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto">
        {/* Back Navigation */}
        <button
          onClick={() => navigate(-1)}
          data-testid="back-to-dashboard"
          className="inline-flex items-center gap-2 text-sm text-[#52525B] hover:text-[#09090B] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Back to Dashboard
        </button>

        {/* Firm Info Card */}
        <div className="bg-white border border-[#E4E4E7] p-6 mb-6" data-testid="firm-info-card">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-2xl tracking-tight font-semibold font-headings text-[#09090B]">
                  {firm.firm_name}
                </h1>
                <button
                  onClick={() => copyToClipboard(firm.firm_name, 'firm_name')}
                  className="text-[#52525B] hover:text-[#0C9168] transition-colors"
                  title="Copy firm name"
                >
                  {copiedField === 'firm_name' ? (
                    <Check className="w-4 h-4 text-[#0C9168]" strokeWidth={2} />
                  ) : (
                    <Copy className="w-4 h-4" strokeWidth={1.5} />
                  )}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={firm.site_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0C9168] hover:text-[#097352] inline-flex items-center gap-1 text-sm"
                  data-testid="site-url-link"
                >
                  {firm.site_url}
                  <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                </a>
                <button
                  onClick={() => copyToClipboard(firm.site_url, 'site_url')}
                  className="text-[#52525B] hover:text-[#0C9168] transition-colors"
                  title="Copy site URL"
                >
                  {copiedField === 'site_url' ? (
                    <Check className="w-4 h-4 text-[#0C9168]" strokeWidth={2} />
                  ) : (
                    <Copy className="w-4 h-4" strokeWidth={1.5} />
                  )}
                </button>
              </div>
            </div>
            <div>
              <span className={`inline-flex items-center px-3 py-1 text-xs font-bold uppercase border ${firm.is_active ? 'bg-green-100 text-green-900 border-green-900' : 'bg-[#F4F4F5] text-[#52525B] border-[#52525B]'}`}>
                {firm.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="border-t border-[#E4E4E7] pt-4 mt-4">
            <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-3">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mb-1">Contact Name</div>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-[#09090B] font-medium">
                    {firm.contact_name || '—'}
                  </div>
                  {firm.contact_name && (
                    <button
                      onClick={() => copyToClipboard(firm.contact_name, 'contact_name')}
                      className="text-[#52525B] hover:text-[#0C9168] transition-colors"
                      title="Copy contact name"
                    >
                      {copiedField === 'contact_name' ? (
                        <Check className="w-3 h-3 text-[#0C9168]" strokeWidth={2} />
                      ) : (
                        <Copy className="w-3 h-3" strokeWidth={1.5} />
                      )}
                    </button>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mb-1">Email Address</div>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-[#09090B]">
                    {firm.contact_email ? (
                      <a 
                        href={`mailto:${firm.contact_email}`} 
                        className="text-[#0C9168] hover:text-[#097352] inline-flex items-center gap-1"
                      >
                        {firm.contact_email}
                        <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                      </a>
                    ) : '—'}
                  </div>
                  {firm.contact_email && (
                    <button
                      onClick={() => copyToClipboard(firm.contact_email, 'contact_email')}
                      className="text-[#52525B] hover:text-[#0C9168] transition-colors"
                      title="Copy email address"
                    >
                      {copiedField === 'contact_email' ? (
                        <Check className="w-3 h-3 text-[#0C9168]" strokeWidth={2} />
                      ) : (
                        <Copy className="w-3 h-3" strokeWidth={1.5} />
                      )}
                    </button>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mb-1">Mobile Number</div>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-[#09090B] font-mono">
                    {firm.contact_mobile ? (
                      <a 
                        href={`tel:${firm.contact_mobile}`} 
                        className="text-[#0C9168] hover:text-[#097352] inline-flex items-center gap-1"
                      >
                        {firm.contact_mobile}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </a>
                    ) : '—'}
                  </div>
                  {firm.contact_mobile && (
                    <button
                      onClick={() => copyToClipboard(firm.contact_mobile, 'contact_mobile')}
                      className="text-[#52525B] hover:text-[#0C9168] transition-colors"
                      title="Copy mobile number"
                    >
                      {copiedField === 'contact_mobile' ? (
                        <Check className="w-3 h-3 text-[#0C9168]" strokeWidth={2} />
                      ) : (
                        <Copy className="w-3 h-3" strokeWidth={1.5} />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 pt-4 mt-4 border-t border-[#E4E4E7]">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mb-1">City</div>
              <div className="text-sm text-[#09090B]">{firm.city || '—'}</div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mb-1">Subscription Days</div>
              <div className="text-sm text-[#09090B] font-mono">{firm.subscription_days || '—'}</div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mb-1">Email Verified</div>
              <div className="text-sm text-[#09090B]">{firm.email_verified ? '✓ Yes' : '✗ No'}</div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mb-1">Mobile Verified</div>
              <div className="text-sm text-[#09090B]">{firm.mobile_verified ? '✓ Yes' : '✗ No'}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mb-1">Support POC</div>
              <div className="text-sm text-[#09090B]">{firm.support_poc || '—'}</div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mb-1">Sales POC</div>
              <div className="text-sm text-[#09090B]">{firm.sales_poc || '—'}</div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mb-1">Setup Status</div>
              <div className="text-sm text-[#09090B]">{firm.setup_done ? '✓ Complete' : '✗ Incomplete'}</div>
            </div>
          </div>

          <div className="flex items-center gap-4 pt-4 border-t border-[#E4E4E7]">
            <a
              href={`https://manage.assuretrial.in/app/allocation-request/${firm.frappe_record_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#0C9168] hover:text-[#097352] inline-flex items-center gap-1"
            >
              View in Frappe →
            </a>
            <span className="text-sm text-[#52525B]">
              First seen: {firm.first_seen_at ? new Date(firm.first_seen_at).toLocaleDateString() : '—'}
            </span>
            <span className="text-sm text-[#52525B]">
              Last synced: {firm.last_synced_at ? new Date(firm.last_synced_at).toLocaleDateString() : '—'}
            </span>
          </div>
        </div>

        {/* Usage Chart */}
        <div className="bg-white border border-[#E4E4E7] p-6 mb-6" data-testid="usage-chart">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl tracking-tight font-medium font-headings text-[#09090B]">
              Daily Event Count
            </h2>
            <div className="flex items-center gap-4">
              {/* View Toggle */}
              <div className="flex items-center gap-2 border border-[#E4E4E7] bg-[#F4F4F5]">
                <button
                  onClick={() => setChartDays(7)}
                  className={`px-3 py-1 text-xs font-bold uppercase transition-colors ${
                    chartDays === 7
                      ? 'bg-[#0C9168] text-white'
                      : 'bg-transparent text-[#52525B] hover:text-[#09090B]'
                  }`}
                >
                  7 Days
                </button>
                <button
                  onClick={() => setChartDays(30)}
                  className={`px-3 py-1 text-xs font-bold uppercase transition-colors ${
                    chartDays === 30
                      ? 'bg-[#0C9168] text-white'
                      : 'bg-transparent text-[#52525B] hover:text-[#09090B]'
                  }`}
                >
                  30 Days
                </button>
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mr-2">
                  {chartDays}-day Total:
                </span>
                <span className="text-xl font-bold font-mono text-[#09090B]">
                  {calculateVisibleTotal(firm.snapshots, chartDays)}
                </span>
              </div>
              <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase border ${getTrendBadgeStyle(firm.trend_label)}`}>
                {firm.trend_label}
              </span>
            </div>
          </div>

          {firm.snapshots && firm.snapshots.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={fillMissingDates(firm.snapshots, chartDays)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis
                  dataKey="snapshot_date"
                  tickFormatter={(date) => format(parseISO(date), 'MMM d')}
                  tick={{ fill: '#52525B', fontSize: 12 }}
                  stroke="#E4E4E7"
                />
                <YAxis
                  tick={{ fill: '#52525B', fontSize: 12 }}
                  stroke="#E4E4E7"
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#09090B',
                    border: 'none',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontFamily: 'JetBrains Mono, monospace'
                  }}
                  labelFormatter={(date) => format(parseISO(date), 'MMM d, yyyy')}
                  formatter={(value) => [value, 'Events']}
                />
                <Line 
                  type="monotone" 
                  dataKey="event_count" 
                  stroke="#0C9168" 
                  strokeWidth={2}
                  dot={{ fill: '#0C9168', r: 3 }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-[#52525B] py-8">No event data available</div>
          )}
        </div>

        {/* Follow-up Log */}
        <div className="bg-white border border-[#E4E4E7] p-6">
          <h2 className="text-xl tracking-tight font-medium font-headings text-[#09090B] mb-4">
            Follow-up history
          </h2>

          {/* Timeline */}
          {firm.follow_ups && firm.follow_ups.length > 0 ? (
            <div className="space-y-4 mb-6" data-testid="follow-up-timeline">
              {firm.follow_ups.map((followUp) => (
                <div key={followUp._id} className="border-l-2 border-[#E4E4E7] pl-4 pb-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-sm font-bold text-[#09090B]">
                        {new Date(followUp.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </div>
                      <div className="text-xs text-[#52525B]">{followUp.assigned_to}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase border ${getOutcomeBadgeStyle(followUp.outcome)}`}>
                        {followUp.outcome.replace('_', ' ')}
                      </span>
                      {canEditDelete(followUp) && (
                        <div className="flex items-center gap-1 ml-2">
                          <button
                            onClick={() => handleEditFollowUp(followUp)}
                            className="p-1 text-[#52525B] hover:text-[#0C9168] transition-colors"
                            title="Edit follow-up"
                          >
                            <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(followUp._id)}
                            className="p-1 text-[#52525B] hover:text-[#DC2626] transition-colors"
                            title="Delete follow-up"
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-[#09090B] mb-2">{followUp.note}</p>
                  {followUp.next_follow_up_date && (
                    <div className="text-xs text-[#52525B]">
                      Next follow-up: {new Date(followUp.next_follow_up_date).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-[#52525B] py-4 mb-6">
              No follow-ups logged yet.
            </div>
          )}

          {/* Add Follow-up Form */}
          <div className="border-t border-[#E4E4E7] pt-6">
            <h3 className="text-lg tracking-tight font-medium font-headings text-[#09090B] mb-4">
              {editingFollowUp ? 'Edit Follow-up' : 'Add Follow-up'}
            </h3>
            {editingFollowUp && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-sm text-blue-900">
                Editing follow-up from {new Date(editingFollowUp.created_at).toLocaleDateString()}
              </div>
            )}
            <form onSubmit={handleSubmitFollowUp} className="space-y-4">
              <div>
                <label htmlFor="note" className="block text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                  Note *
                </label>
                <textarea
                  id="note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  required
                  minLength={10}
                  rows={3}
                  data-testid="follow-up-note-input"
                  className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] focus:ring-offset-2 focus:ring-offset-white text-sm"
                  placeholder="Enter follow-up notes (minimum 10 characters)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="outcome" className="block text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                    Outcome *
                  </label>
                  <select
                    id="outcome"
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                    required
                    data-testid="follow-up-outcome-select"
                    className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                  >
                    <option value="interested">Interested</option>
                    <option value="cold">Cold</option>
                    <option value="purchased">Purchased</option>
                    <option value="no_response">No Response</option>
                    <option value="extended">Extended</option>
                    <option value="deactivated">Deactivated</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="next_follow_up_date" className="block text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                    Next Follow-up Date
                  </label>
                  <input
                    id="next_follow_up_date"
                    type="date"
                    value={nextFollowUpDate}
                    onChange={(e) => setNextFollowUpDate(e.target.value)}
                    data-testid="follow-up-date-input"
                    className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  value={user.name}
                  disabled
                  className="w-full px-4 py-2 border border-[#E4E4E7] bg-[#F4F4F5] text-[#52525B] text-sm cursor-not-allowed"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-600 p-3 text-sm text-red-900">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-follow-up-button"
                  className="bg-[#0C9168] text-white hover:bg-[#097352] px-6 py-2 font-medium transition-colors border border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (editingFollowUp ? 'Updating...' : 'Logging...') : (editingFollowUp ? 'Update follow-up' : 'Log follow-up')}
                </button>
                {editingFollowUp && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-6 py-2 border border-[#E4E4E7] text-[#09090B] hover:bg-[#F4F4F5] font-medium transition-colors"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setDeleteConfirmId(null)}
        >
          <div className="bg-white max-w-md w-full p-6 m-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-[#09090B] mb-4">
              Delete Follow-up?
            </h2>
            <p className="text-sm text-[#52525B] mb-6">
              Are you sure you want to delete this follow-up? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 border border-[#E4E4E7] text-[#09090B] hover:bg-[#F4F4F5]"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteFollowUp(deleteConfirmId)}
                className="px-4 py-2 bg-[#DC2626] text-white hover:bg-[#B91C1C]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}